import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useActionChoices } from "stores";
import { FormSelectInput } from "form";
import { actionInputName } from "components/action-modal/utils/inputName";
import { TASK_INPUT_NAMES } from "constants/action-form";
import { ACTION_STATUS, OPERATIONS, TASKS } from "constants/actions";
import { useActionModalGetContext } from "context/ActionModalProvider";

const DISABLED_BULK_OTHER_OPERATION = [OPERATIONS.OTHER.HARVEST];

const OtherOperationInput = ({ isProposed, isViewMode }) => {
  const { t } = useTranslation();

  const { actions, getOperationById } = useActionChoices();
  const { values, setFieldValue } = useFormikContext();
  const { isBulk, action } = useActionModalGetContext();
  const actionStatus = action?.status;

  const task = TASKS.other;
  const taskInputName = TASK_INPUT_NAMES[task];
  const operationInputName = actionInputName(
    taskInputName,
    "operationTypeId",
    isProposed,
  );

  const checkIfDisabledOperation = useCallback(
    (operationTypeId) => {
      return (
        isBulk &&
        actionStatus === ACTION_STATUS.performed &&
        DISABLED_BULK_OTHER_OPERATION.includes(
          getOperationById(operationTypeId),
        )
      );
    },
    [actionStatus, getOperationById, isBulk],
  );

  const isDisabledOperationChosen = useMemo(
    () => checkIfDisabledOperation(values?.otherAction?.operationTypeId),
    [checkIfDisabledOperation, values?.otherAction?.operationTypeId],
  );

  const options = useMemo(() => {
    const otherOptions = actions.other?.operations?.map(
      ({ operationTypeId, operationTypeName }) => {
        const isDisabled = checkIfDisabledOperation(operationTypeId);
        return {
          value: operationTypeId,
          label: operationTypeName,
          disabled: isDisabled,
        };
      },
    );
    const sorted = otherOptions.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
    return sorted;
  }, [actions, checkIfDisabledOperation]);

  // check whether the selected operation is now disabled
  useEffect(() => {
    if (isDisabledOperationChosen) {
      setFieldValue(operationInputName, "");
    }
  }, [isDisabledOperationChosen, operationInputName, setFieldValue]);

  return (
    <FormSelectInput
      name={operationInputName}
      placeholder={t("OperationInput.Other.Placeholder")}
      helperText={
        isDisabledOperationChosen &&
        t("ActionModal.BulkDisabled.PerformedOperation")
      }
      shouldHaveErrorBehaviorAfterSubmit
      isViewMode={isViewMode}
      options={options}
    />
  );
};

export default OtherOperationInput;
